import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, Truck, MapPin, CheckCircle } from 'lucide-react';
import { CONTACT_INFO } from '../data/mockData';
import SEO from '../components/SEO';

const MobileDetailing = () => {
  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Mobile Car Detailing Simi Valley | Detailing Disciples"
        description="Paint-safe mobile maintenance detailing with deionized water, fresh exterior towels and forced-air drying in Simi Valley and nearby areas."
        canonical="https://thedetailingdisciples.com/mobile-detailing"
      />

      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center mb-4">
              <Truck className="w-16 h-16 text-[#B8860B]" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              Mobile Detailing in <span className="text-[#B8860B]">Simi Valley</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Premium mobile service with the same paint-safe methods: deionized water, brand-new exterior microfiber towels, forced-air drying, and owner-operated attention. We bring the detail to your home or office.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Mobile Service <span className="text-[#B8860B]">Benefits</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Save Time</h3>
                <p className="text-gray-400 text-sm">No need to drop off or wait. We come to your location.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Same Quality</h3>
                <p className="text-gray-400 text-sm">Same paint-safe methods and owner-operated care.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Flexible Scheduling</h3>
                <p className="text-gray-400 text-sm">Work from home? We detail while you work.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Wide Coverage</h3>
                <p className="text-gray-400 text-sm">Serving Ventura County and select LA areas.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Service <span className="text-[#B8860B]">Areas</span>
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <p className="text-gray-300 mb-6 text-center">
                Mobile service is available in the following areas. Travel availability varies by service type, location, and schedule.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {CONTACT_INFO.serviceAreas.map((city) => (
                  <div key={city} className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-[#B8860B] flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{city}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Mobile vs. <span className="text-[#B8860B]">Garage Service</span>
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-4 text-gray-300">
              <p>
                <strong className="text-white">Mobile service</strong> is ideal for maintenance detailing, exterior washes, and interior cleaning where convenience is the priority.
              </p>
              <p>
                <strong className="text-white">Appointment-only garage service</strong> may be recommended for paint correction and ceramic coating when controlled lighting, weather protection, and cure conditions make that the better choice.
              </p>
              <p className="text-[#B8860B] font-semibold">
                We'll discuss the best option based on your service, vehicle, and location during the quote process.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#111216] mb-4">
            Check Mobile Service Availability
          </h2>
          <p className="text-[#111216]/80 text-lg mb-8 max-w-2xl mx-auto">
            Share your location, service interest, and vehicle details. We'll confirm availability and provide a quote.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold">
              <Calendar className="w-5 h-5 mr-2" />
              Request Mobile Service Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MobileDetailing;